// OVO-027 — shared helpers for computing the effective / captured weight
// of an order from its products + collected items. Used by the order detail
// header (OrderDetail.jsx), the orders CSV export (orderCsvExport.js), and
// anywhere else that needs to disambiguate the dispatcher-declared
// `Order.weight` from the driver/operator-captured value.
//
// Sources of weight, in priority order, per OrderProduct:
//   1. `OrderProduct.weight`  — dispatcher pre-filled OR operator override
//                               (the OVO-014 editor writes here)
//   2. matching CollectedItem.weight — driver capture at pickup
//
// Nullish (not falsy) comparisons throughout so `0` — the legitimate
// OVO-017 "nothing on site" capture — is treated as a real value, not
// a missing one.

/**
 * Compute the order-level captured weight by summing the per-product
 * effective weights (priority: OrderProduct.weight, then matching
 * CollectedItem.weight). Returns null when no product carries a captured
 * weight.
 *
 * @param {object} order  An order object as returned by the orders API.
 *                        Expects `orderProducts[]` (with `partnerItemType`)
 *                        and `collectedItems[]` (PRODUCT-scoped on the
 *                        list payload — see OVO-019).
 * @returns {{value: number, driverOnly: boolean, driverName: string|null}|null}
 */
export function computeCapturedWeight(order) {
  const products = order?.orderProducts || [];
  if (products.length === 0) return null;
  const collectedItems = order.collectedItems || [];

  let sum = 0;
  let hasAny = false;
  let driverOnly = true;
  let driverName = null;

  for (const op of products) {
    if (!op?.partnerItemType?.requiresWeight) continue;
    const dispatchWeight = op.weight;
    // Resolve the OrderProduct's partner-item-type id from either the
    // top-level FK (detail endpoint projects it) OR the nested object
    // (list endpoint only projects the nested `partnerItemType.id` —
    // not the top-level `partnerItemTypeId`). Without this fallback,
    // captured-weight lookups silently failed for the orders CSV and
    // anywhere else that reads from the list payload.
    const opPartnerItemTypeId = op.partnerItemTypeId ?? op.partnerItemType?.id;
    const matching = collectedItems.find((ci) => ci.partnerItemTypeId === opPartnerItemTypeId);
    const driverWeight = matching?.weight ?? null;
    const fromDriver = dispatchWeight == null && driverWeight != null;
    const effective = dispatchWeight != null ? dispatchWeight : driverWeight;
    if (effective == null) continue;
    sum += effective;
    hasAny = true;
    if (!fromDriver) driverOnly = false;
    if (fromDriver && !driverName) {
      driverName = matching?.collectedByUser?.name || matching?.collectedByUser?.email || null;
    }
  }

  if (!hasAny) return null;
  // Round to 2 decimals so multi-product sums don't surface float noise.
  const value = Math.round(sum * 100) / 100;
  return { value, driverOnly, driverName };
}

/**
 * Convenience helper for places that just want "the best number we have"
 * for an order's weight — `Order.weight` if set, else the captured value
 * from `computeCapturedWeight`, else null.
 *
 * Used by orderCsvExport.js so a driver-weighted order (dispatcher left
 * `Order.weight` blank, driver captured `0.6` at pickup) doesn't show as
 * an empty cell in the CSV.
 */
export function effectiveOrderWeight(order) {
  if (order?.weight != null) return order.weight;
  const captured = computeCapturedWeight(order);
  return captured ? captured.value : null;
}

/**
 * Flatten the driver-captured product info into CSV-friendly scalars.
 *
 * Returns:
 *   - `productTypes`: semicolon-joined list of distinct PartnerItemType
 *     names that were captured (e.g. "OM" / "OM; Compost")
 *   - `weight`: sum of captured weights (excludes dispatcher-only products
 *     to keep this a "what the driver actually weighed" number)
 *   - `unit`: the common unit of measure (e.g. "kg") — null if mixed
 *   - `capturedBy`: semicolon-joined list of distinct names/emails of
 *     people who captured (typically one driver)
 *   - `capturedAt`: the latest collectedAt timestamp (ISO) — null if none
 *
 * Returns null when no PRODUCT-category CollectedItem on the order — i.e.
 * the dispatcher pre-filled all weights or nothing was captured at pickup.
 */
export function summarizeCapturedItems(order) {
  const collectedItems = (order?.collectedItems || []).filter(
    (ci) => ci.weight != null && ci.partnerItemType?.category !== 'EMPTY',
  );
  if (collectedItems.length === 0) return null;

  const typeNames = new Set();
  const units = new Set();
  const capturers = new Set();
  let weightSum = 0;
  let latestAt = null;

  for (const ci of collectedItems) {
    const name = ci.partnerItemType?.name;
    if (name) typeNames.add(name);
    const unit = ci.partnerItemType?.unitOfMeasure;
    if (unit) units.add(unit);
    weightSum += ci.weight ?? 0;
    const who = ci.collectedByUser?.name || ci.collectedByUser?.email;
    if (who) capturers.add(who);
    if (ci.collectedAt) {
      const t = new Date(ci.collectedAt).getTime();
      if (!latestAt || t > latestAt) latestAt = t;
    }
  }

  return {
    productTypes: [...typeNames].join('; '),
    weight: Math.round(weightSum * 100) / 100,
    unit: units.size === 1 ? [...units][0] : null,
    capturedBy: [...capturers].join('; '),
    capturedAt: latestAt ? new Date(latestAt).toISOString() : null,
  };
}
